import React, { useRef, useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { useDispatch } from 'react-redux';
import { OptionGroup } from 'react-querybuilder';
import { useLocation, useNavigate, useParams } from 'react-router-dom'; 

import {
  AlertState,
  DataProfile,
  AlarmMetadata,
  AlarmDefinition,
  DataSourceAlarmMetadata,
  SelectedQueryBuilderField,
} from 'types/types';
import { showAlert, updateDirtyState} from 'features/applicationState';
import NIService, { INIService } from 'services/NIService';
import { ProfileOption } from './TargetCriteria/DataProfiles';

import {
  hasQueryBuilderEmptyValues,
  isQueryBuilderInInitMode,
} from '../QueryBuilder/helper';
import AlarmModuleService, {
  IAlarmModuleService,
} from 'services/AlarmModuleService';
import FormDetails, {
  Ref as AlarmManagementFormDetailsRef,
} from './FormDetails/FormDetails';
import FormRules, {
  Ref as AlarmManagementFormRulesRef,
} from './FormRules/FormRules';
import TargetCriteria, {
  Ref as AlarmManagementTargetCriteriaRef,
} from './TargetCriteria/TargetCriteria';
import { NotificationType } from './Output/OutputElements';
import InstancesCreationCriteria, {
  InstancesCreationCriteriaRef,
} from './InstancesCreationCriteria/InstancesCreationCriteria';
import Output, { Ref as AlarmMangementOutputRef } from './Output/Output';
import FormStepper, { Ref as AlarmFormStepperRef } from './FormStepper';
import AlarmManagementPopup, { AlarmManagementPopupRef } from '../Popup/Popup';
import { ConstructionOutlined } from '@mui/icons-material';

export interface ValidationError {
  details: DetailsFormValidationError;
  rules: RulesFormValidationError;
  targetCriteria: TargetCriteriaValidationError;
  instancesCreationCriteria: InstancesCreationCriteriaError;
  output: OutputValidationError;
}
export interface DetailsFormValidationError {
  name: string;
  frequency: string;
  niDataSource: string;
  executionGroup: string;
  executionPriority: string;
}

export interface RulesFormValidationError {
  queryBuilder: string;
  aggregationLevel: string;
  studyIntervals: string;
}

export interface InstancesCreationCriteriaError {
  consecutivePeriod: string;
  breachedIntervals: string;
  latestInterval: string;
}

export interface TargetCriteriaValidationError {
  dataProfile: string;
  queryBuilder: string;
}

export interface OutputValidationError {
  subject: string;
  recipients: string;
}

const CreateForm: React.FC = () => {
  const navigate = useNavigate();

  const { state } = useLocation();

  const { id: alarmId } = useParams();

  const dispatch = useDispatch();

  const offsetDeduction = 180;

  const [alarmDefinitionData, setAlarmDefinitionData] =
    useState<AlarmDefinition | null>(null);
  const [loadingQueryBuilderData, setLoadingQueryBuilderData] =
    useState<boolean>(false);
  const [dataSourceMetadataForAlarm, setDataSourceMetadataForAlarm] =
    useState<DataSourceAlarmMetadata>();
  const [alarmMetaData, setAlarmMetadata] = useState<AlarmMetadata>();
  const [alarmQueryOperators, setAlarmQueryOperators] = useState<OptionGroup[]>(
    []
  );

  const rulesRef = useRef<HTMLDivElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const instancesCreationCriteriaRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const formContentRef = useRef<HTMLDivElement>(null);
  const targetCriteriaRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<number | null>(null);
  const disableScrollStepChange = useRef<boolean>(false);
  const popupRef = useRef<AlarmManagementPopupRef>(null);
  const stepperRef = useRef<AlarmFormStepperRef | null>(null);
  const selectedRulesRef = useRef<SelectedQueryBuilderField>({
    rules: [],
    targetCriteria: [],
  });

  const alarmManagementFormDetailsRef =
    useRef<AlarmManagementFormDetailsRef | null>(null);
  const alarmManagementFormRulesRef =
    useRef<AlarmManagementFormRulesRef | null>(null);
  const alarmManagementTargetCriteriaRef =
    useRef<AlarmManagementTargetCriteriaRef | null>(null);
  const alarmManagementInstancesCreationCriteriaRef =
    useRef<InstancesCreationCriteriaRef | null>(null);
  const alarmOutputRef = useRef<AlarmMangementOutputRef | null>(null);

  const fetchMetadataForAlarm = async (dataSourceId: string) => {
    const niService: INIService = new NIService();

    try {
      setLoadingQueryBuilderData(true);
      niService
        .GetDataSourceMetadataForAlarm(dataSourceId)
        .then((data) => {
          setDataSourceMetadataForAlarm(data);
        })
        .catch(() => {
          const alertData: AlertState = {
            severity: 'error',
            title: 'NI Data Source Data',
            message: 'An error has occurred!',
            open: true,
          };
          dispatch(showAlert(alertData));
        })
        .finally(() => {
          setLoadingQueryBuilderData(false);
        });
    } catch (error) {
      console.error('Failed to fetch alarm metadata', error);
    }
  };

  const fetchAlarmDefinitionById = async (alarmId: number) => {
    const niService: IAlarmModuleService = new AlarmModuleService();
    try {
      niService.GetAlarmDefinitionById(alarmId).then((data) => {
        setAlarmDefinitionData(data);
        if (data?.dsp_id) {
          fetchMetadataForAlarm(data.dsp_id);
          fetchDataProfilesByDataSourceId(data.dsp_id);
        }
      });
    } catch (error) {
      console.error('Failed to fetch alarm data', error);
    }
  };

  const fetchAlarmDefinitionToDuplicateByOriginalId = async (
    alarmToDuplicateId: number
  ) => {
    const niService: IAlarmModuleService = new AlarmModuleService();
    try {
      niService.GetDuplicateAlarmDefinition(alarmToDuplicateId).then((data) => {
        setAlarmDefinitionData(data);
        if (data?.dsp_id) {
          fetchMetadataForAlarm(data.dsp_id);
          fetchDataProfilesByDataSourceId(data.dsp_id);
        }
      });
    } catch (error) {
      console.error('Failed to fetch alarm data for duplication', error);
    }
  };

  const fetchAlarmsMetadata = async () => {
    const niService: IAlarmModuleService = new AlarmModuleService();
    try {
      niService.GetAlarmMetadata().then((data) => {
        setAlarmMetadata(data);
      });
    } catch (error) {
      console.error('Failed to fetch alarm metadata', error);
    }
  };

  const fetchAlarmsQueryOperators = async () => {
    const niService: IAlarmModuleService = new AlarmModuleService();
    try {
      niService.GetAlarmQueryOperators().then((data) => {
        setAlarmQueryOperators(data);
      });
    } catch (error) {
      console.error('Failed to fetch alarm query operators', error);
    }
  };

  const fetchDataProfilesByDataSourceId = async (dataSourceId: string) => {
    const niService: INIService = new NIService();
    try {
      niService
        .GetDataProfilesByDataSourceId(dataSourceId)
        .then((data: { profiles: DataProfile[]; users: string[] }) => {
          alarmManagementTargetCriteriaRef.current?.handleChangeDataProfiles(
            data.profiles
          );
        });
    } catch (error) {
      console.error('Failed to fetch alarm rules count', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const parsedConsecutivePeriods =
        alarmManagementInstancesCreationCriteriaRef?.current?.consecutiveBreach
          ?.current?.consecutivePeriod.current?.value
          ? parseInt(
              alarmManagementInstancesCreationCriteriaRef.current
                ?.consecutiveBreach.current?.consecutivePeriod.current?.value
            )
          : undefined;

      const parsednumberOfBreachedInterval = parseInt(
        (
          alarmManagementInstancesCreationCriteriaRef.current
            ?.breachingPeriodsChecks?.current?.breachNumber
            ?.current as HTMLInputElement | null
        )?.value || '-1'
      );

      const parsednumberOfLatestIntervals = parseInt(
        (
          alarmManagementInstancesCreationCriteriaRef.current
            ?.breachingPeriodsChecks?.current?.latestIntervalNumber
            ?.current as HTMLInputElement | null
        )?.value || '-1'
      );

      const parsedStudyIntervals = alarmManagementFormRulesRef?.current?.refs
        .studyIntervals?.current?.studyIntervals.current?.value
        ? parseInt(
            alarmManagementFormRulesRef.current?.refs.studyIntervals?.current
              ?.studyIntervals.current?.value
          )
        : undefined;

      const timeAggregation =
        alarmManagementFormDetailsRef.current?.refs.frequency.current?.value ??
        '';

      const alarmExpiration =
        alarmManagementFormDetailsRef.current?.refs.alarmExpiration.current
          ?.value ?? null;

      const dataProfileSID = alarmManagementTargetCriteriaRef.current?.refs
        .alarmManagementDataProfilesRef.current?.dataProfileRef.current?.value
        ? parseInt(
            alarmManagementTargetCriteriaRef.current?.refs
              .alarmManagementDataProfilesRef.current?.dataProfileRef.current
              ?.value
          )
        : null;

      const selectedDataProfileOption =
        alarmManagementTargetCriteriaRef.current?.refs
          .alarmManagementDataProfilesRef.current?.selectedProfileOption ??
        ProfileOption.CUSTOM;

      var formattedDate: string | null = null;
      if (alarmExpiration && alarmExpiration != null && alarmExpiration != '') {
        const [month, day, year] = alarmExpiration.split('/');
        formattedDate = `${year}-${month}-${day}`; // Convert to "YYYY-MM-DD"
      }

      const body: AlarmDefinition = {
        id: alarmId ? +alarmId : undefined,
        name:
          alarmManagementFormDetailsRef.current?.refs.name.current?.value ?? '',
        alarm_expiration: formattedDate,
        description:
          alarmManagementFormDetailsRef.current?.refs.description.current
            ?.value ?? '',
        dsp_id:
          alarmManagementFormDetailsRef.current?.refs.niDataSource.current
            ?.value ?? '',
        severity:
          alarmManagementFormDetailsRef.current?.refs.severity.current?.refs.getSeverity() ??
          '',
        execution_group:
          alarmManagementFormDetailsRef.current?.refs.executionGroup.current
            ?.value ?? '',
        execution_priority:
          alarmManagementFormDetailsRef.current?.refs.executionPriority.current
            ?.value ?? '',
        target_element_criteria_serialized:
          selectedDataProfileOption === ProfileOption.CUSTOM
            ? alarmManagementTargetCriteriaRef.current?.refs.queryBuilder
                .current?.query
            : null,
        alarm_rule_serialized:
          alarmManagementFormRulesRef.current?.refs.queryBuilder.current?.query,
        consecutive_periods: parsedConsecutivePeriods,
        enabled: true,
        time_aggregation_level: timeAggregation,
        object_aggregation_level:
          alarmManagementFormRulesRef.current?.refs?.aggregationLevel?.current
            ?.value ?? '',
        study_intervals: parsedStudyIntervals,
        data_profile_sid: dataProfileSID,
        output_serialized:
          alarmOutputRef.current?.refs.outputElements.current?.notificationType
            .current?.value === NotificationType.EMAIL
            ? {
                notificationType:
                  alarmOutputRef.current?.refs.outputElements.current
                    ?.notificationType.current?.value ?? '',
                recipients:
                  alarmOutputRef.current?.refs.outputElements.current
                    ?.recipients,
                subject:
                  alarmOutputRef.current?.refs.outputElements.current?.subject
                    .current?.value ?? '',
                attributes:
                  alarmOutputRef.current?.refs.virtualizedTransferList.current
                    ?.rightListRef.current?.items ?? [],
              }
            : undefined,
        execution_rules_serialized: {
          weekendsExcluded:
            alarmManagementFormDetailsRef.current?.refs.excludeWeekends.current
              ?.isChecked ?? false,
          useDayOverDay:
            alarmManagementFormRulesRef.current?.refs.studyIntervals?.current
              ?.dayOverDay.current?.isChecked ?? false,
          useBusyHourKPIValue:
            alarmManagementFormRulesRef.current?.refs.busyHour?.current
              ?.busyHour.current?.isChecked ?? false,
          instancesCreationPeriodCriteria:
            alarmManagementInstancesCreationCriteriaRef.current
              ?.breachingPeriodsChecks.current?.breachingSelected
              ? {
                  numberOfBreachedInterval: parsednumberOfBreachedInterval,
                  numberOfLatestIntervals: parsednumberOfLatestIntervals,
                }
              : undefined,
        },
      };

      const queryBuilderValidationTargetCriteria = hasQueryBuilderEmptyValues(
        body.target_element_criteria_serialized,
        dataSourceMetadataForAlarm
      );
      const queryBuilderValidationRule = hasQueryBuilderEmptyValues(
        body.alarm_rule_serialized,
        dataSourceMetadataForAlarm
      );

      const initialErrors: ValidationError = {
        details: {
          name: body.name === '' ? "Alarm Name can't be empty" : '',
          executionPriority:
            body.execution_priority === ''
              ? "Execution Priority can't be empty"
              : '',
          niDataSource:
            body.dsp_id === '' ? "NI Data Source can't be empty" : '',
          frequency: timeAggregation === '' ? "Frequency can't be empty" : '',
          executionGroup:
            body.execution_group !== '' && body.execution_group.trim() === ''
              ? 'Please enter a valid execution group'
              : '',
        },
        rules: {
          aggregationLevel:
            body.object_aggregation_level === ''
              ? "Aggregation Level can't be empty"
              : '',
          queryBuilder:
            isQueryBuilderInInitMode(body.alarm_rule_serialized) ||
            queryBuilderValidationRule.validationMessage != ''
              ? queryBuilderValidationRule.validationMessage
              : queryBuilderValidationRule.hasEmptyValue
              ? 'Query Builder has missing fields'
              : !queryBuilderValidationRule.hasKPIorPIorVendorCounter
              ? "Query Builder doesn't contain KPI, PI, KQI, or Custom KPI"
              : alarmManagementFormRulesRef.current?.refs.queryBuilder.current
                  ?.isNumberOfDistinctRuleValid.current
              ? `Maximum amount of ${alarmManagementFormRulesRef.current?.refs.queryBuilder.current?.isNumberOfDistinctRuleValid.current} field reached.`
              : '',
          studyIntervals:
            alarmManagementFormRulesRef.current?.refs.studyIntervals?.current
              ?.studyIntervals.current != null && //studyIntervals not null, so it's showing and needs to be validated
            (body.study_intervals === undefined ||
              body.study_intervals === null ||
              body.study_intervals <= 0)
              ? 'Invalid Study Interval Value'
              : '',
        },
        targetCriteria: {
          queryBuilder:
            selectedDataProfileOption === ProfileOption.CUSTOM
              ? isQueryBuilderInInitMode(
                  body.target_element_criteria_serialized
                ) || queryBuilderValidationTargetCriteria.hasEmptyValue
                ? 'Query Builder has missing fields'
                : ''
              : '',
          dataProfile:
            selectedDataProfileOption === ProfileOption.PROFILE &&
            !dataProfileSID
              ? 'Please select a data profile'
              : '',
        },
        instancesCreationCriteria: {
          consecutivePeriod:
            alarmManagementInstancesCreationCriteriaRef.current
              ?.consecutiveBreach?.current?.consecutiveBreach &&
            body.consecutive_periods === undefined
              ? "Consecutive Period can't be empty"
              : '',
          breachedIntervals:
            alarmManagementInstancesCreationCriteriaRef.current
              ?.breachingPeriodsChecks.current?.breachingSelected &&
            body.execution_rules_serialized?.instancesCreationPeriodCriteria
              ?.numberOfBreachedInterval === -1
              ? "Breaching interval can't be empty"
              : alarmManagementInstancesCreationCriteriaRef.current
                  ?.validationErrors.breachedIntervals
              ? alarmManagementInstancesCreationCriteriaRef.current
                  ?.validationErrors.breachedIntervals
              : '',
          latestInterval:
            alarmManagementInstancesCreationCriteriaRef.current
              ?.breachingPeriodsChecks.current?.breachingSelected &&
            body.execution_rules_serialized?.instancesCreationPeriodCriteria
              ?.numberOfLatestIntervals === -1
              ? "Latest Interval number can't be empty"
              : alarmManagementInstancesCreationCriteriaRef.current
                  ?.validationErrors.latestInterval
              ? alarmManagementInstancesCreationCriteriaRef.current
                  ?.validationErrors.latestInterval
              : '',
        },
        output: {
          subject:
            body.output_serialized?.subject === ''
              ? "Subject can't be empty"
              : '',
          recipients:
            body.output_serialized?.recipients?.to.length === 0
              ? "Recipients can't be empty"
              : '',
        },
      };

      const hasDetailsErrors = Object.values(initialErrors.details).some(
        (error) => error !== ''
      );
      const hasOutputErrors = Object.values(initialErrors.output).some(
        (error) => error !== ''
      );
      const hasRulesErrors = Object.values(initialErrors.rules).some(
        (error) => error !== ''
      );
      const hasTargetCriteriaErrors = Object.values(
        initialErrors.targetCriteria
      ).some((error) => error !== '');
      const hasInstancesCreationCriteriaErros = Object.values(
        initialErrors.instancesCreationCriteria
      ).some((error) => error !== '');

      if (hasDetailsErrors) {
        alarmManagementFormDetailsRef.current?.detailsValidation(
          initialErrors.details
        );
      }
      if (hasRulesErrors) {
        alarmManagementFormRulesRef.current?.refs.rulesValidation(
          initialErrors.rules
        );
        alarmManagementFormRulesRef.current?.refs.queryBuilderValidation(
          initialErrors.rules
        );
      }
      if (hasTargetCriteriaErrors) {
        alarmManagementTargetCriteriaRef.current?.refs.targetCriteriaValidation(
          initialErrors.targetCriteria
        );
      }
      if (hasInstancesCreationCriteriaErros) {
        alarmManagementInstancesCreationCriteriaRef.current?.instancesValidation(
          initialErrors.instancesCreationCriteria
        );
      }
      if (hasOutputErrors) {
        alarmOutputRef.current?.refs.outputElements.current?.outputValidation(
          initialErrors.output
        );
      }
      let firstErrorElement:
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLDivElement
        | undefined
        | null;
      if (initialErrors.details.name) {
        firstErrorElement =
          alarmManagementFormDetailsRef.current?.refs.name?.current;
      } else if (initialErrors.details.executionPriority) {
        firstErrorElement =
          alarmManagementFormDetailsRef.current?.refs.executionPriority.current
            ?.node;
      } else if (initialErrors.details.niDataSource) {
        firstErrorElement =
          alarmManagementFormDetailsRef.current?.refs.niDataSource?.current
            ?.node;
      } else if (initialErrors.details.frequency) {
        firstErrorElement =
          alarmManagementFormDetailsRef.current?.refs.frequency?.current?.node;
      } else if (
        selectedDataProfileOption === ProfileOption.PROFILE &&
        !firstErrorElement &&
        initialErrors.targetCriteria.dataProfile
      ) {
        firstErrorElement =
          alarmManagementTargetCriteriaRef.current?.refs
            .alarmManagementDataProfilesRef?.current?.dataProfileRef.current
            ?.node;
      } else if (
        selectedDataProfileOption === ProfileOption.CUSTOM &&
        !firstErrorElement &&
        initialErrors.targetCriteria.queryBuilder
      ) {
        firstErrorElement =
          alarmManagementTargetCriteriaRef.current?.refs.queryBuilder?.current
            ?.container;
      } else if (!firstErrorElement && initialErrors.rules.aggregationLevel) {
        firstErrorElement =
          alarmManagementFormRulesRef.current?.refs.aggregationLevel?.current
            ?.node;
      } else if (!firstErrorElement && initialErrors.rules.queryBuilder) {
        firstErrorElement =
          alarmManagementFormRulesRef.current?.refs.queryBuilder.current
            ?.container;
      } else if (
        !firstErrorElement &&
        alarmManagementFormRulesRef.current?.refs.studyIntervals?.current
          ?.studyIntervals.current != null && //studyIntervals not null, so it's showing and needs to be validated
        initialErrors.rules.studyIntervals
      ) {
        firstErrorElement =
          alarmManagementFormRulesRef.current?.refs.studyIntervals?.current
            ?.studyIntervals.current;
      } else if (
        !firstErrorElement &&
        initialErrors.instancesCreationCriteria.consecutivePeriod
      ) {
        firstErrorElement =
          alarmManagementInstancesCreationCriteriaRef?.current
            ?.consecutiveBreach?.current?.consecutivePeriod.current?.node;
      } else if (
        !firstErrorElement &&
        initialErrors.instancesCreationCriteria.breachedIntervals
      ) {
        firstErrorElement =
          alarmManagementInstancesCreationCriteriaRef?.current
            ?.breachingPeriodsChecks.current?.breachNumber.current;
      } else if (
        !firstErrorElement &&
        initialErrors.instancesCreationCriteria.latestInterval
      ) {
        firstErrorElement =
          alarmManagementInstancesCreationCriteriaRef?.current
            ?.breachingPeriodsChecks.current?.latestIntervalNumber.current;
      } else if (!firstErrorElement && initialErrors.output.subject) {
        firstErrorElement =
          alarmOutputRef?.current?.refs.outputElements.current?.subject.current;
      } else if (!firstErrorElement && initialErrors.output.recipients) {
        firstErrorElement =
          alarmOutputRef?.current?.refs.outputElements.current
            ?.recipientContainer.current;
      }
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      if (
        !hasRulesErrors &&
        !hasDetailsErrors &&
        !hasTargetCriteriaErrors &&
        !hasInstancesCreationCriteriaErros &&
        !hasOutputErrors
      ) {
        const niService: IAlarmModuleService = new AlarmModuleService();
        try {
          dispatch(updateDirtyState(false));
          let toastTitle: string;
          let toastMessage: string;
          if (alarmId) {
            await niService.EditAlarmDefinition(body);
            toastTitle = 'Alarm Edit';
            toastMessage = 'Alarm has been edited successfully!';
          } else {
            const duplictate =
              (state &&
                state.alarmIdToDuplicate &&
                state.alarmIdToDuplicate != -1) ||
              false;
            await niService.CreateAlarmDefinition(body, duplictate);
            toastTitle = 'Alarm Creation';
            toastMessage = 'Alarm has been created successfully!';
          }
          const alertData: AlertState = {
            severity: 'success',
            title: toastTitle,
            message: toastMessage,
            open: true,
          };
          setTimeout(() => {
            dispatch(showAlert(alertData));
          }, 800); 
          navigate('/view/alarm-management-view');
        } catch (error: any) {
          let alertData: AlertState;
          let toastTitle: string;
          if (alarmId) {
            toastTitle = 'Alarm Edit';
          } else {
            toastTitle = 'Alarm Creation';
          }
          if (error?.response?.status === 420) {
            alertData = {
              severity: 'warning',
              title: toastTitle,
              message: error?.response?.data,
              open: true,
            };
          } else {
            alertData = {
              severity: 'error',
              title: toastTitle,
              message: 'An error has occurred!',
              open: true,
            };
          }
          setTimeout(() => {
            dispatch(showAlert(alertData));
          }, 800);
        }
      }
    } catch (error) {
      const alertData: AlertState = {
        severity: 'error',
        title: 'Alarm Creation',
        message: 'An error has occurred!',
        open: true,
      };
      dispatch(showAlert(alertData));
    }
  };

  const handleStepClick = (index: number) => {
    stepperRef.current?.setActiveStep(index);
    let targetElement: HTMLDivElement | null = null;
    switch (index) {
      case 0:
        targetElement = detailsRef.current;
        break;
      case 1:
        targetElement = targetCriteriaRef.current;
        break;
      case 2:
        targetElement = rulesRef.current;
        break;
      case 3:
        targetElement = instancesCreationCriteriaRef.current;
        break;
      case 4:
        targetElement = outputRef.current;
        break;
      default:
        break;
    }

    if (targetElement && formContentRef.current) {
      formContentRef.current.scrollTo({
        top: targetElement.offsetTop - offsetDeduction,
        behavior: 'smooth',
      });
    }
    disableScrollStepChange.current = true;
  };

  const handleScroll = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = window.setTimeout(() => {
      if (!disableScrollStepChange.current) {
        if (!formContentRef.current) return;

        const sectionPositions = [
          {
            index: 0,
            top:
              (detailsRef.current?.getBoundingClientRect().top || 0) -
              offsetDeduction,
          },
          {
            index: 1,
            top:
              (targetCriteriaRef.current?.getBoundingClientRect().top || 0) -
              offsetDeduction,
          },
          {
            index: 2,
            top: (rulesRef.current?.getBoundingClientRect().top || 0) - 300,
          },
          {
            index: 3,
            top:
              (instancesCreationCriteriaRef.current?.getBoundingClientRect()
                .top || 0) - 450,
          },
          {
            index: 4,
            top: (outputRef.current?.getBoundingClientRect().top || 0) - 600,
          },
        ];

        const closestSection = sectionPositions.reduce((prev, curr) =>
          Math.abs(curr.top) < Math.abs(prev.top) ? curr : prev
        );

        if (closestSection.index !== stepperRef.current?.activeStep) {
          stepperRef.current?.setActiveStep(closestSection.index);
        }
      } else {
        disableScrollStepChange.current = false;
      }
    }, 100);
  };

  const handlePageTitle = () => {
    if (alarmId) {
      return 'Edit Alarm';
    }

    if (state && state.alarmIdToDuplicate && state.alarmIdToDuplicate != -1) {
      return 'Clone Alarm';
    }

    return 'Create a new alarm';
  };

  useEffect(() => {
    const container = formContentRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepperRef.current?.activeStep]);

  useEffect(() => {   
    dispatch(updateDirtyState(true));
    if (alarmId) {
      fetchAlarmDefinitionById(+alarmId);
    } else if (
      state &&
      state.alarmIdToDuplicate &&
      state.alarmIdToDuplicate !== -1
    ) {
      fetchAlarmDefinitionToDuplicateByOriginalId(+state.alarmIdToDuplicate);
    }
    fetchAlarmsMetadata();
    fetchAlarmsQueryOperators();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="alarm-form">
      <div className="main-container">
        <div>
          <span className="main-container-title">{handlePageTitle()}</span>
        </div>
        <div className="alarm-form-container">
          <div className="stepper-container">
            <FormStepper onStepClick={handleStepClick} ref={stepperRef} />
          </div>
          <div className="form-content" ref={formContentRef}>
            <div ref={detailsRef}>
              <FormDetails
                alarmMetaData={alarmMetaData}
                fetchDataProfilesByDataSourceId={
                  fetchDataProfilesByDataSourceId
                }
                ref={alarmManagementFormDetailsRef}
                alarmDefinitionData={alarmDefinitionData}
                fetchMetadataForAlarm={fetchMetadataForAlarm}
                loadingQueryBuilderData={loadingQueryBuilderData}
                dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
                alarmManagementFormRulesRef={alarmManagementFormRulesRef}
                alarmManagementInstancesCreationCriteriaRef={
                  alarmManagementInstancesCreationCriteriaRef
                }
              />
            </div>
            <div ref={targetCriteriaRef}>
              <TargetCriteria
                alarmOutputRef={alarmOutputRef}
                selectedRulesRef={selectedRulesRef}
                queryOperators={alarmQueryOperators}
                ref={alarmManagementTargetCriteriaRef}
                alarmDefinitionData={alarmDefinitionData}
                loadingQueryBuilderData={loadingQueryBuilderData}
                dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
                hasQueryBuilderEmptyValues={hasQueryBuilderEmptyValues}
                alarmManagementFormRulesRef={alarmManagementFormRulesRef}
                alarmManagementFormDetailsRef={alarmManagementFormDetailsRef}
              />
            </div>
            <div ref={rulesRef}>
              <FormRules
                alarmMetaData={alarmMetaData}
                alarmOutputRef={alarmOutputRef}
                ref={alarmManagementFormRulesRef}
                alarmManagementTargetCriteriaRef={
                  alarmManagementTargetCriteriaRef
                }
                selectedRulesRef={selectedRulesRef}
                queryOperators={alarmQueryOperators}
                alarmDefinitionData={alarmDefinitionData}
                loadingQueryBuilderData={loadingQueryBuilderData}
                hasQueryBuilderEmptyValues={hasQueryBuilderEmptyValues}
                dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
              />
            </div>
            <div ref={instancesCreationCriteriaRef}>
              <InstancesCreationCriteria
                alarmDefinitionData={alarmDefinitionData ?? null}
                alarmMetaData={alarmMetaData}
                dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
                ref={alarmManagementInstancesCreationCriteriaRef}
              />
            </div>
            <div ref={outputRef}>
              <Output
                ref={alarmOutputRef}
                alarmMetaData={alarmMetaData}
                alarmDefinitionData={alarmDefinitionData}
                loadingQueryBuilderData={loadingQueryBuilderData}
                dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
                alarmManagementFormRulesRef={alarmManagementFormRulesRef}
              />
            </div>
          </div>
        </div>
        <div className="form-buttons-container">
          <Button
            variant="contained"
            className="cancel-button"
            onClick={() => {
              popupRef.current?.handleOpenPopup(true);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="create-button"
            onClick={handleSubmit}
          >
            {!alarmId ? 'Create' : 'Save'}
          </Button>
        </div>
      </div>
      <AlarmManagementPopup
        title="Are you sure you want to leave?"
        handleCancelButtonClick={() => {
          popupRef.current?.handleOpenPopup(false);
        }}
        handleCloseButtonClick={() => {   
          dispatch(updateDirtyState(false));
          setTimeout(() => {
            navigate('/view/alarm-management-view');
          }, 10);
        }}
        ref={popupRef}
      />
    </div>
  );
};

export default CreateForm;
